import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewChildren } from '@angular/core';
import { ParticipantApiService } from '../../api/participant-api.service';
import { DocumentModel } from '../../models/document.model';
import { DynamicFormLayout } from '../../models/dynamic-form-layout.model';
import { ParticipantModel } from '../../models/participant.model';
import { SignatureField } from '../../models/signature-field.model';
import { ContentBuilderFieldType } from '../../types/content-builder-field.type';
// import { ContentBuilderCacheService } from '@app/modules/content-builder/services/content-builder-cache.service';
// import { FieldService } from '@app/modules/content-builder/services/field.service';
// import { DateTimePipe } from '@app/shared/pipes/date-time.pipe';
import { AddSignatureModalComponent } from '../add-signature-modal/add-signature-modal.component';
import { SigningType, TokenInfo } from '../document-signing.type';
import { PdfViewerComponent } from '@core/pdf-viewer/feature';
import { SignatureBoxComponent } from '../signature-box/signature-box.component';
import { TextLayerRendered } from '@core/pdf-viewer/data-access';
import { SignatureBoxData, SingpassPositionData } from './signing-on-pdf.type';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'portal-signing-on-pdf',
  templateUrl: './signing-on-pdf.component.html',
  styleUrls: ['./signing-on-pdf.component.scss'],
  // providers: [
  //     FieldService,
  //     ContentBuilderCacheService,
  //     DateTimePipe
  // ]
})
export class SigningOnPdfComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Input() document!: DocumentModel;
  @Input() tokenInfo!: TokenInfo;
  @Input() accessToken!: string;
  @Input() signingTypes: SigningType[] = ['drawSignature'];
  @Input() isMobileMode!: boolean;
  @Output() completed = new EventEmitter<SignatureBoxData[]>();
  @Output() isMobileModeChange = new EventEmitter<any>();

  @ViewChild(PdfViewerComponent, { static: false }) pdfViewerComponent!: PdfViewerComponent;
  @ViewChildren(SignatureBoxComponent) signatureBoxesComponent: any;
  @ViewChildren(SignatureBoxComponent, { read: ElementRef }) signatureBoxesRef: any;
  @ViewChild('pdfContainerRef') pdfContainerRef!: ElementRef;
  signatureFields: Array<{ foundPosition: boolean; data: SignatureField; participant: ParticipantModel }> = [];
  signatureBoxes: Array<{ position: any; key: any; markupId: any; detail: any }> = [];
  scale = 1;
  signatureData: SignatureBoxData[] = [];
  participant!: ParticipantModel;
  loading = true;
  countUnlockSignatureBox = 0;
  countSignedSignatureBox = 0;
  unlockSignatureBoxIndexes: number[] = [];
  nextSignatureBoxIndex = 0;
  showIndicator = false;
  contractPDF: any;
  a4Width = 794;
  singpassPositions: Array<SingpassPositionData> = [];
  signatureBoxLoaded = new Set<string>();

  constructor(private participantApiService: ParticipantApiService) {}

  ngOnInit(): void {
    this.loadContractPDF();
    this.loadSignatureFields();
    setTimeout(() => this.isMobileModeChange.emit(true), 0);
  }

  loadContractPDF() {
    this.participantApiService
      .getContractPDF(this.accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.contractPDF = res;
          this.loading = false;
        },
        error: error => {
          this.loading = false;
        },
      });
  }

  loadSignatureFields() {
    const layouts = this.document.fields.layouts as { [x in ContentBuilderFieldType]?: DynamicFormLayout[] };
    if (layouts && layouts.signatureField) {
      layouts.signatureField.forEach(signtureItem => {
        const property = this.document.fields.properties[signtureItem.key] as SignatureField;
        const participant = property && this.document.participants.find(item => item.markupId === property.config.markupId);
        if (participant && participant.status !== 'confirmed') {
          this.signatureFields.push({
            foundPosition: false,
            data: property,
            participant: participant,
          });
          if (this.tokenInfo.uuid === participant.markupId) {
            this.signatureData.push({
              signature: null as any,
              signatureBoxId: property.key,
            });
          }
        }
      });
    }
  }

  allTextLayersRendered(args: Set<TextLayerRendered>): void {
    this.loadSignaturesPosition(Array.from(args));

    setTimeout(() => {
      this.signatureBoxesComponent._results.forEach((component: any, i: number) => {
        if (!component.isLocked) {
          this.unlockSignatureBoxIndexes.push(i);
        }
      });
      this.countUnlockSignatureBox = this.unlockSignatureBoxIndexes.length;
      if (this.countUnlockSignatureBox) {
        this.showIndicator = true;
      }
    });

    if (this.document.docType !== 'invoice') {
      this.loadSignaturePositionByPoint();
    }
  }

  loadSignaturesPosition(textLayerRendereds: TextLayerRendered[]): void {
    this.loadSignaturePositionByTextLayer();
    if (this.signatureBoxes.length < this.signatureFields.length) {
      textLayerRendereds.forEach(pageRenderInfo => {
        this.loadSignaturePositionByPdfContent(pageRenderInfo);
      });
    }
  }

  loadSignaturePositionByTextLayer(): void {
    const nativeElement = this.pdfViewerComponent.pdfViewerRef.nativeElement;
    const pdfContainerRect = nativeElement.getBoundingClientRect();
    const spanCollections: HTMLCollectionOf<HTMLSpanElement> = nativeElement.getElementsByTagName('span');

    Array.from<HTMLSpanElement>(spanCollections).forEach(element => {
      if (element.children?.length === 0) {
        for (let i = 0; i < this.signatureFields.length; i++) {
          if (
            element.textContent?.includes(this.signatureFields[i].data.key) &&
            !this.signatureBoxLoaded.has(this.signatureFields[i].data.key)
          ) {
            this.signatureFields[i].foundPosition = true;
            const elementRect = element.getBoundingClientRect();
            const position = {
              'top.px': elementRect.top - pdfContainerRect.top,
              'left.px': elementRect.left - pdfContainerRect.left,
            };

            this.signatureBoxes.push({
              position: position,
              key: this.signatureFields[i].data.key,
              markupId: this.signatureFields[i].data.config.markupId,
              detail: this.signatureFields[i].participant,
            });
            this.signatureBoxLoaded.add(this.signatureFields[i].data.key);
            break;
          }
        }
      }
    });
  }

  loadSignaturePositionByPdfContent(pageRenderInfo: any) {
    const source = pageRenderInfo.source;
    const unloadSignatureFields = this.signatureFields.filter(signatureField => signatureField.foundPosition === false);
    const pdfContainerRect = this.pdfContainerRef.nativeElement.getBoundingClientRect();
    const textContentItemsStr: any = {};
    source.textContentItemsStr.forEach((str: string, i: number) => {
      str = str.replace(/\s/g, '');
      if (str !== '') {
        textContentItemsStr[i] = str;
      }
    });
    const itemStrByRow: any = {};
    Object.keys(textContentItemsStr).forEach(key => {
      itemStrByRow[source.textDivs[key].style.top] = itemStrByRow[source.textDivs[key].style.top] ?? [];
      itemStrByRow[source.textDivs[key].style.top].push({ key: key, str: textContentItemsStr[key] });
    });
    const mergeStrByRow: any = {};
    Object.keys(itemStrByRow).forEach(key => {
      let str = '';
      const keys: any = [];
      itemStrByRow[key].forEach((item: any) => {
        str += item.str;
        keys.push(item.key);
      });
      mergeStrByRow[key] = { keys: keys, str: str };
    });
    const validRows: any = [];
    Object.keys(mergeStrByRow).forEach(key => {
      this.signatureFields.forEach(signatureField => {
        if (mergeStrByRow[key].str.includes(signatureField.data.key)) {
          signatureField.foundPosition = true;
          validRows.push({
            markupId: signatureField.data.config.markupId,
            key: signatureField.data.key,
            keys: mergeStrByRow[key].keys,
            str: mergeStrByRow[key].str,
            signatureField: signatureField,
          });
        }
      });
    });
    validRows.forEach((row: any) => {
      const index = row.str.indexOf(row.key);
      const preStr = row.str.substr(0, index);
      let startAt = row.keys[0];
      let offsetLeft = 0;
      if (preStr.length) {
        // calulate offset left
        let count = 0;
        let endAt;
        for (let i = 0; i < row.keys.length; i++) {
          count += textContentItemsStr[row.keys[i]].length;
          endAt = row.keys[i];
          if (count >= preStr.length) {
            startAt = row.keys[i + 1];
            break;
          }
        }
        const numOverCharacters = count - preStr.length;
        if (numOverCharacters > 0) {
          // tslint:disable-next-line:max-line-length
          const numTrimmedCharacters = source.textDivs[endAt].textContent.replace(/\s/g, '').length - textContentItemsStr[endAt].length;
          const numOffsetLeftCharacters = numOverCharacters + numTrimmedCharacters;
          offsetLeft = (source.textDivs[endAt].offsetWidth / source.textDivs[endAt].textContent.length) * numOffsetLeftCharacters;
        }
        // end calculate
      }

      const elementRect = source.textDivs[startAt].getBoundingClientRect();
      const position = {
        'top.px': elementRect.top - pdfContainerRect.top,
        'left.px': elementRect.left - pdfContainerRect.left - offsetLeft,
      };
      this.signatureBoxes.push({
        position: position,
        key: row.signatureField.data.key,
        markupId: row.markupId,
        detail: row.signatureField.participant,
      });
    });
  }

  convertPosition(items: any, viewport: any) {
    return items.map((item: any) => {
      const tm = item.transform;
      let x = tm[4];
      let y = viewport.height - tm[5];
      if (viewport.rotation === 90) {
        x = tm[5];
        y = tm[4];
      }
      // see https://github.com/mozilla/pdf.js/issues/8276
      const height = Math.sqrt(tm[2] * tm[2] + tm[3] * tm[3]);
      return {
        x: x,
        y: y,
        str: item.str,
        dir: item.dir,
        width: item.width,
        height: height,
        fontName: item.fontName,
      };
    });
  }

  scaleChange(args: number): void {
    this.scale = args;
  }

  goToSignatureBox() {
    this.signatureBoxesRef._results[
      this.unlockSignatureBoxIndexes[this.nextSignatureBoxIndex]
    ].nativeElement.firstElementChild.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    this.nextSignatureBoxIndex =
      this.nextSignatureBoxIndex === this.unlockSignatureBoxIndexes.length - 1 ? 0 : this.nextSignatureBoxIndex + 1;
  }

  signed(key: any, data: SignatureBoxData) {
    const signature: any = this.signatureData.find(item => item.signatureBoxId === key);
    signature.signature = data.signature;
    signature.signatureImage = data.signatureImage;
    signature.signaturePosition = data.signaturePosition;
    this.countSignedSignatureBox = this.signatureData.filter(item => !!item.signature).length;
    if (this.validate()) {
      this.completed.emit(this.signatureData);
    }
  }

  validate(): boolean {
    return this.countSignedSignatureBox === this.countUnlockSignatureBox;
  }

  loadSignaturePositionByPoint() {
    const pdfContainerRect = this.pdfContainerRef.nativeElement.getBoundingClientRect();
    const pages = Array.from(document.querySelectorAll('.page')).filter(page => page.getAttribute('data-page-number'));
    this.singpassPositions = this.signatureBoxes
      .filter(signatureBox => signatureBox.markupId === this.tokenInfo.uuid)
      .map(signatureBox => {
        let top = 0;
        let elementRect: any;
        const page = pages.find(p => {
          elementRect = p.getBoundingClientRect();
          top = signatureBox.position['top.px'] - (elementRect.top - pdfContainerRect.top);
          return top < elementRect.height;
        });
        const y = elementRect ? elementRect.height - top : 0;
        return {
          signatureBoxId: signatureBox.key,
          // @ts-ignore
          pageNumber: +page.getAttribute('data-page-number'),
          signaturePosition: {
            // manual + -
            x: this.pixelToPoint(signatureBox.position['left.px']) - 1,
            y: this.pixelToPoint(y - AddSignatureModalComponent.defaultSignatureSize.height) - 10,
            width: this.pixelToPoint(AddSignatureModalComponent.defaultSignatureSize.width),
            height: this.pixelToPoint(AddSignatureModalComponent.defaultSignatureSize.height),
          },
        };
      });
  }

  pixelToPoint(pixel: number) {
    return (pixel * 72) / 96;
  }

  // 1pt = 1/72 inch
  // 1 inch = 96 pixcel
  // 96pixel
  // 8.271 inch = 794picel
  // 1*8.271 / 794/72*
  // 794 / 8.271

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
